import React, { useEffect, useState } from "react";
import { api } from "../../../../lib/api";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../../../../components/ui/alert-dialog";
import { Shield } from "@phosphor-icons/react";
import { toast } from "sonner";
import { CashAbuseChip } from "./CashAbuseChip";
import { ChangePasswordButton } from "./ChangePasswordButton";
import { ReconcileStuckCardsPanel } from "./ReconcileStuckCardsPanel";
import { RegenerateQrPanel } from "./RegenerateQrPanel";
import { LostCardsPanel } from "./LostCardsPanel";
import { AbandonedCartsPanel } from "./AbandonedCartsPanel";
import { WalletAdoptionTile } from "../WalletAdoptionTile";
import { IssuanceProviderTile } from "../IssuanceProviderTile";
import { InstallFunnelTile } from "../InstallFunnelTile";

export function DeploymentHealthCard() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  useEffect(() => {
    api.get("/admin/config-health").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  const revokeAll = async () => {
    setRevoking(true);
    try {
      const r = await api.post("/admin/sessions/revoke-all");
      if (r.data?.token) {
        if (sessionStorage.getItem("cr_token")) {
          sessionStorage.setItem("cr_token", r.data.token);
        } else {
          localStorage.setItem("cr_token", r.data.token);
        }
      }
      toast.success("All sessions revoked. Your session continues with a fresh token.");
      setConfirmOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not revoke sessions");
    } finally {
      setRevoking(false);
    }
  };

  if (!data) return null;

  const dotFor = (s) =>
    s === "ok" ? "bg-[#184A30]" : s === "warn" ? "bg-[#CBA35C]" : "bg-[#D96C4A]";
  const textFor = (s) =>
    s === "ok" ? "text-[#184A30]" : s === "warn" ? "text-[#8C6B21]" : "text-[#B04E2F]";
  const overallLabel =
    data.overall === "ok" ? "Healthy" : data.overall === "warn" ? "Needs attention" : "Action required";
  const overallBg =
    data.overall === "ok"
      ? "bg-[#184A30]/5 border-[#184A30]/20"
      : data.overall === "warn"
      ? "bg-[#CBA35C]/10 border-[#CBA35C]/40"
      : "bg-[#D96C4A]/10 border-[#D96C4A]/40";

  return (
    <div className={`rounded-2xl p-5 border ${overallBg}`} data-testid="deployment-health-card">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <span className={`inline-flex w-10 h-10 rounded-full ${dotFor(data.overall)} text-white items-center justify-center shrink-0`}>
            <Shield size={18} weight="duotone" />
          </span>
          <div>
            <p className="serif text-xl text-[#112318]">Deployment health · {overallLabel}</p>
            <p className="text-xs text-[#4A5C50] mt-0.5 mono">
              {data.summary.ok} ok · {data.summary.warn} warn · {data.summary.error} error
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <ChangePasswordButton />
          <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <AlertDialogTrigger asChild>
              <button type="button"
                className="text-xs px-3 py-1.5 rounded-lg border border-[#D96C4A]/40 text-[#B04E2F] hover:bg-[#D96C4A]/10"
                data-testid="revoke-all-sessions-btn">
                Sign out all sessions
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="revoke-all-sessions-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Sign out every session?</AlertDialogTitle>
                <AlertDialogDescription>
                  Every user signed into Zcardon right now (admins, managers, customers, impersonation sessions)
                  will be forced to sign in again on their next action.
                  <br /><br />
                  Your own admin session is preserved with a fresh token — you won't be logged out.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="revoke-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={revokeAll} disabled={revoking}
                  className="bg-[#D96C4A] hover:bg-[#B04E2F] text-white" data-testid="revoke-confirm">
                  {revoking ? "Revoking…" : "Revoke all sessions"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <button type="button" onClick={() => setOpen((v) => !v)}
            className="text-xs text-[#184A30] link-ul"
            data-testid="deployment-health-toggle">
            {open ? "Hide details" : "Show details"}
          </button>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="deployment-health-chips">
        {data.checks.map((c) => {
          const klass = `inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border ${
            c.status === "ok"
              ? "bg-[#184A30]/5 border-[#184A30]/30 text-[#184A30]"
              : c.status === "warn"
              ? "bg-[#CBA35C]/10 border-[#CBA35C]/40 text-[#8C6B21]"
              : "bg-[#D96C4A]/10 border-[#D96C4A]/40 text-[#B04E2F]"
          }`;
          if (c.key === "abuse") {
            return <CashAbuseChip key={c.key} check={c} klass={klass} dotClass={dotFor(c.status)} />;
          }
          return (
            <span key={c.key} title={`${c.note} · ${c.value}`} className={klass}
              data-testid={`deployment-chip-${c.key}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${dotFor(c.status)}`} aria-hidden="true" />
              {c.label}
            </span>
          );
        })}
      </div>

      <WalletAdoptionTile />
      <IssuanceProviderTile />
      <InstallFunnelTile />

      {open && (
        <ul className="mt-5 divide-y divide-[#E5E0D8] bg-white border border-[#E5E0D8] rounded-xl" data-testid="deployment-health-list">
          {data.checks.map((c) => (
            <li key={c.key} className="flex items-start gap-3 p-4" data-testid={`health-${c.key}`}>
              <span className={`w-2.5 h-2.5 rounded-full mt-2 shrink-0 ${dotFor(c.status)}`} aria-hidden="true" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#112318] flex items-center gap-2">
                  {c.label}
                  <span className={`text-[10px] tracking-[0.2em] uppercase ${textFor(c.status)}`}>{c.status}</span>
                </p>
                <p className="text-xs text-[#4A5C50] mt-1">{c.note}</p>
                <p className="text-[11px] mono text-[#4A5C50] mt-0.5 truncate">{c.value}</p>
              </div>
              {c.action_url && c.action_label && (
                <a
                  href={c.action_url}
                  target={c.action_url.startsWith("http") ? "_blank" : undefined}
                  rel={c.action_url.startsWith("http") ? "noopener noreferrer" : undefined}
                  className={`shrink-0 text-[11px] font-medium px-3 py-1.5 rounded-lg whitespace-nowrap ${
                    c.status === "error"
                      ? "bg-[#D96C4A] text-white hover:bg-[#B04E2F]"
                      : c.status === "warn"
                      ? "bg-[#CBA35C] text-white hover:bg-[#8C6B21]"
                      : "border border-[#184A30]/30 text-[#184A30] hover:bg-[#184A30]/5"
                  }`}
                  data-testid={`health-action-${c.key}`}
                >
                  {c.action_label} →
                </a>
              )}
            </li>
          ))}
        </ul>
      )}
      <ReconcileStuckCardsPanel />
      <RegenerateQrPanel />
      <LostCardsPanel />
      <AbandonedCartsPanel />
    </div>
  );
}
